import { Link } from "react-router-dom";
import {
  Phone,
  ShieldCheck,
  Sparkles,
  Clock,
  MapPin,
  CheckCircle2,
  Building2,
  Dumbbell,
  Store,
  GraduationCap,
  ArrowRight,
  Brush,
  Droplets,
  WashingMachine,
  Calendar,
  Headphones,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BRAND, PHOTOS, CONTACT } from "@/lib/brand";

const audiences = [
  {
    label: "Offices",
    desc: "Productive, presentable workspaces — cleaned around your hours.",
    img: PHOTOS.office,
    Icon: Building2,
  },
  {
    label: "Gyms",
    desc: "Hygienic, sweat-free facilities your members will notice.",
    img: PHOTOS.gym,
    Icon: Dumbbell,
  },
  {
    label: "Retail Shops",
    desc: "Spotless storefronts that turn first impressions into sales.",
    img: PHOTOS.retail,
    Icon: Store,
  },
  {
    label: "Schools",
    desc: "Safe, sanitised classrooms and shared spaces, every day.",
    img: PHOTOS.cleaningPro,
    Icon: GraduationCap,
  },
];

const services = [
  {
    title: "Regular Contract Cleaning",
    highlight: true,
    desc: "Weekly, bi-weekly or daily — a dependable team and a checklist tailored to your premises.",
    Icon: Brush,
    bullets: ["Same trusted cleaners", "Quality checks", "Flexible hours"],
  },
  {
    title: "Deep Cleaning",
    desc: "Top-to-bottom resets for kitchens, washrooms and high-traffic areas. Ideal seasonally or pre-inspection.",
    Icon: WashingMachine,
    bullets: ["Sanitisation", "Hard-floor care", "Detail work"],
  },
  {
    title: "Window Cleaning",
    desc: "Streak-free interior and exterior glass — keeping your storefront and offices bright.",
    Icon: Droplets,
    bullets: ["Interior + exterior", "High-reach safe", "Scheduled rounds"],
  },
];

const whyUs = [
  "Reliable, consistent cleaning every visit",
  "Flexible schedules — including evenings & weekends",
  "Fully insured, vetted Cork-based team",
  "Fast response times to requests & issues",
  "Single point of contact, transparent reporting",
];

const steps = [
  {
    n: "01",
    title: "Request a quote",
    desc: "Tell us about your space in 60 seconds. No pressure, no obligation.",
    Icon: Calendar,
  },
  {
    n: "02",
    title: "We assess your needs",
    desc: "A friendly site visit or call to scope the job and recommend the right plan.",
    Icon: Headphones,
  },
  {
    n: "03",
    title: "We handle the cleaning",
    desc: "Your dedicated team gets to work on a schedule that suits you.",
    Icon: Sparkles,
  },
];

const faqs = [
  {
    q: "How much does commercial cleaning cost?",
    a: "Pricing depends on the size of your premises, frequency of cleaning and the scope of work. Most office and retail clients are surprised at how affordable a regular contract is. Send us your details and we'll come back with a clear, no-obligation quote — usually within one working day.",
  },
  {
    q: "Do you bring your own supplies and equipment?",
    a: "Yes — by default we supply all professional-grade cleaning products and equipment. If you'd prefer eco-friendly products or a specific brand, just let us know and we'll accommodate.",
  },
  {
    q: "What areas of Cork do you cover?",
    a: "We serve businesses across Cork City and County — including the city centre, Douglas, Ballincollig, Mahon, Little Island, Carrigaline, Blackrock and surrounding areas. Not sure if we cover yours? Just ask.",
  },
  {
    q: "How quickly can you start?",
    a: "For most jobs we can start within a few days of your free site assessment. Need it sooner? Call 085 8828 656 — we'll do everything we can to help.",
  },
  {
    q: "Are you fully insured?",
    a: "Yes. Clean Me carries full public liability and employer's insurance. We're happy to share certificates on request.",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 bg-dotted opacity-60 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 reveal">
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-[#4CAF50]" />
                Cork-based · Available now
              </div>
              <h1
                data-testid="hero-headline"
                className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]"
              >
                Professional cleaning for{" "}
                <span className="text-[#4CAF50]">Cork businesses</span>{" "}
                done right, every time.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
                We handle your cleaning so you can focus on running your
                business. Reliable, flexible and hassle-free.
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <p
                  data-testid="hero-urgency"
                  className="inline-flex items-center gap-2 font-semibold text-[#388E3C]"
                >
                  <Clock className="h-4 w-4" />
                  Get a quote in under 60 seconds
                </p>
                <span
                  data-testid="hero-response-time"
                  className="inline-flex items-center gap-2 text-slate-600"
                >
                  <span className="h-1 w-1 rounded-full bg-slate-400" />
                  Average response time: under 24 hours
                </span>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/contact">
                  <Button
                    data-testid="hero-quote-btn"
                    className="h-12 px-7 rounded-full bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold btn-primary-glow"
                  >
                    Get Your Free Quote
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${CONTACT.phoneTel}`}>
                  <Button
                    data-testid="hero-call-btn"
                    variant="outline"
                    className="h-12 px-6 rounded-full border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold"
                  >
                    <Phone className="h-4 w-4 mr-2 text-[#4CAF50]" />
                    Call {CONTACT.phone}
                  </Button>
                </a>
              </div>

              <div
                data-testid="hero-trust-strip"
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-600"
              >
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#4CAF50]" />
                  Fully insured
                </span>
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#4CAF50]" />
                  Local Cork business
                </span>
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#4CAF50]" />
                  Reliable & consistent service
                </span>
              </div>
            </div>

            <div className="lg:col-span-5 reveal reveal-delay-2">
              <div className="relative">
                {/* Soft green glow behind the mascot — transparent PNG sits directly on light background */}
                <div
                  aria-hidden
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <div className="h-72 w-72 sm:h-96 sm:w-96 rounded-full bg-[#4CAF50]/15 blur-3xl" />
                </div>
                <img
                  src={BRAND.mascot}
                  alt="Friendly Clean Me cleaner mascot"
                  className="relative z-10 mx-auto w-full h-[440px] sm:h-[560px] lg:h-[600px] object-contain drop-shadow-[0_25px_45px_rgba(15,23,42,0.18)]"
                  draggable="false"
                />

                {/* Floating trust card */}
                <div className="hidden sm:flex absolute left-2 bottom-2 z-20 items-center gap-3 rounded-2xl bg-white border border-slate-200 shadow-soft-lg px-4 py-3">
                  <div className="h-10 w-10 rounded-full bg-[#4CAF50]/15 inline-flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-[#388E3C]" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">
                      Trusted by Cork businesses
                    </p>
                    <p className="text-sm font-semibold text-slate-900">
                      Reliable. Insured. Local.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Authority strip — under hero */}
          <div
            data-testid="hero-authority-strip"
            className="mt-12 sm:mt-16 rounded-3xl bg-white border border-slate-200 shadow-soft px-6 sm:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <p className="font-display text-base sm:text-lg font-semibold text-slate-900 inline-flex items-center gap-2.5">
              <Sparkles className="h-5 w-5 text-[#4CAF50]" />
              Trusted by businesses across Cork
            </p>
            <p className="text-sm text-slate-600 sm:text-right">
              Reliable, consistent and fully insured cleaning.
            </p>
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section
        data-testid="section-audiences"
        className="bg-slate-50 border-y border-slate-200"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C]">
              Who we work with
            </p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Cleaning that fits your business — not the other way around.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {audiences.map(({ label, desc, img, Icon }) => (
              <article
                key={label}
                data-testid={`audience-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-soft hover:shadow-soft-lg transition-all duration-300"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={img}
                    alt={label}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute left-4 bottom-4 inline-flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-[#388E3C] shadow-sm">
                      <Icon className="h-4.5 w-4.5" />
                    </span>
                    <span className="font-display text-xl font-bold text-white">
                      {label}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {desc}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        data-testid="section-services"
        className="bg-white"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 max-w-4xl">
            <div>
              <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C]">
                Our services
              </p>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
                Three core services. Zero hassle.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1 hover:gap-2 transition-all"
            >
              View all services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {services.map(({ title, desc, Icon, bullets, highlight }) => (
              <article
                key={title}
                data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative rounded-3xl p-7 border transition-all duration-300 ${
                  highlight
                    ? "bg-slate-900 text-white border-slate-900 shadow-soft-lg"
                    : "bg-white border-slate-200 shadow-soft hover:shadow-soft-lg"
                }`}
              >
                {highlight && (
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1 rounded-full bg-[#4CAF50] text-white text-xs font-semibold px-2.5 py-1">
                    Most popular
                  </span>
                )}
                <div
                  className={`h-12 w-12 rounded-2xl inline-flex items-center justify-center ${
                    highlight ? "bg-[#4CAF50]/20 text-[#4CAF50]" : "bg-[#4CAF50]/10 text-[#388E3C]"
                  }`}
                >
                  <Icon className="h-6 w-6" />
                </div>
                <h3
                  className={`font-display mt-5 text-xl sm:text-2xl font-bold tracking-tight ${
                    highlight ? "text-white" : "text-slate-900"
                  }`}
                >
                  {title}
                </h3>
                <p
                  className={`mt-3 leading-relaxed ${
                    highlight ? "text-slate-300" : "text-slate-600"
                  }`}
                >
                  {desc}
                </p>
                <ul className="mt-5 space-y-2">
                  {bullets.map((b) => (
                    <li
                      key={b}
                      className={`flex items-center gap-2 text-sm ${
                        highlight ? "text-slate-200" : "text-slate-700"
                      }`}
                    >
                      <CheckCircle2 className="h-4 w-4 text-[#4CAF50]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Link to="/contact" className="mt-6 inline-block">
                  <Button
                    data-testid={`service-card-cta-${title.toLowerCase().replace(/\s+/g, "-")}`}
                    variant={highlight ? "default" : "outline"}
                    className={`rounded-full ${
                      highlight
                        ? "bg-[#4CAF50] hover:bg-[#43A047] text-white"
                        : "border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    Get a quote <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section
        data-testid="section-why-us"
        className="bg-slate-50 border-y border-slate-200"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C]">
              Why choose us
            </p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Why businesses choose Clean Me.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
              We're not the biggest cleaning company in Cork — and we don't want
              to be. We're the one your team can count on every single visit.
            </p>
            <ul className="mt-8 space-y-4">
              {whyUs.map((point) => (
                <li
                  key={point}
                  className="flex items-start gap-3 text-slate-800"
                >
                  <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#4CAF50]/15 text-[#388E3C] flex-shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </span>
                  <span className="font-medium">{point}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="mt-8 inline-block">
              <Button
                data-testid="why-us-cta"
                className="rounded-full h-12 px-7 bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold btn-primary-glow"
              >
                Get Your Free Quote
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="lg:col-span-6">
            <div className="relative grid grid-cols-2 gap-4">
              <div className="rounded-3xl overflow-hidden shadow-soft-lg col-span-2">
                <img
                  src={PHOTOS.cleaningPro}
                  alt="Clean Me cleaning professional at work"
                  className="w-full h-72 sm:h-80 object-cover"
                />
              </div>
              <div className="rounded-3xl bg-white border border-slate-200 p-5 flex items-center justify-center overflow-hidden h-44 relative">
                <div
                  aria-hidden
                  className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-[#4CAF50]/20 blur-2xl"
                />
                <img
                  src={BRAND.mascot}
                  alt="Clean Me mascot giving a thumbs up"
                  className="relative z-10 h-full object-contain"
                />
              </div>
              <div className="rounded-3xl bg-white border border-slate-200 p-5 shadow-soft">
                <Wallet className="h-7 w-7 text-[#388E3C]" />
                <p className="mt-3 font-display text-xl font-bold text-slate-900">
                  Transparent pricing
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Clear quotes. No hidden fees. No long lock-ins.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        data-testid="section-how-it-works"
        className="bg-white"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C]">
              How it works
            </p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Three simple steps to a cleaner business.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {steps.map(({ n, title, desc, Icon }, i) => (
              <div
                key={n}
                data-testid={`how-step-${i + 1}`}
                className="relative rounded-3xl bg-white border border-slate-200 p-7 shadow-soft hover:shadow-soft-lg transition-all"
              >
                <span
                  aria-hidden
                  className="absolute right-5 top-3 font-display text-7xl font-extrabold text-[#4CAF50]/12 select-none"
                  style={{ color: "rgba(76,175,80,0.12)" }}
                >
                  {n}
                </span>
                <div className="h-12 w-12 rounded-2xl inline-flex items-center justify-center bg-slate-900 text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display mt-5 text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                  {title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST SECTION */}
      <section
        data-testid="section-trust"
        className="bg-slate-50 border-y border-slate-200"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                Icon: MapPin,
                title: "Trusted across Cork",
                desc: "Serving offices, gyms, retail and schools city-wide and across the county.",
              },
              {
                Icon: ShieldCheck,
                title: "Fully insured professionals",
                desc: "Public liability and employer's insurance in place. Vetted, trained team.",
              },
              {
                Icon: Sparkles,
                title: "Consistent, high quality",
                desc: "Defined cleaning specs. Same trusted team. Quality checks every visit.",
              },
            ].map(({ Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`trust-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-3xl bg-white border border-slate-200 p-7 shadow-soft"
              >
                <div className="h-12 w-12 rounded-2xl bg-[#4CAF50]/12 inline-flex items-center justify-center text-[#388E3C]">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display mt-5 text-xl font-bold text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR PROMISE */}
      <section data-testid="section-our-promise" className="bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C]">
              Our promise
            </p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              What every Clean Me client gets — every visit.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                Icon: ShieldCheck,
                title: "Reliable & consistent",
                desc: "Same trusted team. Defined cleaning spec. No surprises.",
              },
              {
                Icon: Clock,
                title: "Flexible scheduling",
                desc: "Weekly, daily, evenings or weekends — whatever suits you.",
              },
              {
                Icon: Headphones,
                title: "Easy communication",
                desc: "One direct contact. Quick replies. Simple reporting.",
              },
              {
                Icon: Sparkles,
                title: "Hassle-free service",
                desc: "We supply, manage and quality-check. You focus on business.",
              },
            ].map(({ Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`promise-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="relative rounded-3xl bg-white border border-slate-200 p-7 shadow-soft hover:shadow-soft-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="h-12 w-12 rounded-2xl bg-[#4CAF50]/12 inline-flex items-center justify-center text-[#388E3C]">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-display mt-5 text-lg sm:text-xl font-bold text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>

          <p
            data-testid="local-trust-line"
            className="mt-10 text-center text-sm sm:text-base text-slate-600"
          >
            <MapPin className="inline-block h-4 w-4 text-[#4CAF50] mr-1.5 -mt-0.5" />
            Proudly serving businesses across Cork and surrounding areas.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="section-faq"
        className="bg-slate-50 border-t border-slate-200"
      >
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-20 sm:py-24">
          <p className="text-sm font-semibold tracking-widest uppercase text-[#388E3C] text-center">
            FAQs
          </p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight text-center">
            Quick answers
          </h2>
          <Accordion
            type="single"
            collapsible
            className="mt-10 divide-y divide-slate-200 rounded-3xl border border-slate-200 bg-white shadow-soft px-2"
          >
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`item-${i}`}
                className="px-4"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="py-5 text-left font-display font-semibold text-slate-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="section-final-cta" className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <div className="relative rounded-[28px] bg-black overflow-hidden">
            <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-[#4CAF50]/30 blur-3xl" />
            <div className="absolute -bottom-24 -right-10 h-80 w-80 rounded-full bg-[#4CAF50]/20 blur-3xl" />
            <div className="relative grid lg:grid-cols-12 items-center gap-8 p-8 sm:p-12 lg:p-16">
              <div className="lg:col-span-7 text-white">
                <p className="text-sm font-semibold tracking-widest uppercase text-[#A5D6A7]">
                  Ready when you are
                </p>
                <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                  Get your free cleaning quote today.
                </h2>
                <p className="mt-4 text-slate-300 max-w-xl">
                  No obligation. We'll respond quickly — usually within one
                  working day — with a tailored plan and a clear price.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/contact">
                    <Button
                      data-testid="final-cta-quote-btn"
                      className="h-12 px-7 rounded-full bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold btn-primary-glow"
                    >
                      Request Your Quote in 60 Seconds
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                  <a href={`tel:${CONTACT.phoneTel}`}>
                    <Button
                      data-testid="final-cta-call-btn"
                      variant="outline"
                      className="h-12 px-6 rounded-full border-white/20 text-white hover:bg-white/10 font-semibold bg-transparent"
                    >
                      <Phone className="h-4 w-4 mr-2 text-[#4CAF50]" />
                      {CONTACT.phone}
                    </Button>
                  </a>
                </div>

                <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#4CAF50]" />
                    Fully insured
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#4CAF50]" />
                    Local Cork business
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-[#4CAF50]" />
                    Reliable & consistent
                  </span>
                </div>
              </div>
              <div className="lg:col-span-5 flex justify-center lg:justify-end">
                <img
                  src={BRAND.mascot}
                  alt="Clean Me mascot"
                  className="h-72 sm:h-96 object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
